import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, BookOpen, CalendarClock, DoorOpen, GraduationCap, Lock, Pencil, Plus, Users } from "lucide-react";
import { OnAirSign } from "../components/OnAirSign";
import { BuyButton } from "../components/BuyButton";
import { CourseDetailDialog, CourseDialog, EditRoomDialog, ScheduleShowDialog } from "../components/lobby/Dialogs";
import { ShowCalendar } from "../components/lobby/ShowCalendar";
import { ROOM_ART } from "../lib/roomArt";
import { money, useApi, loadFailed } from "../lib/api";
import { useMe } from "../lib/me";

const RoomDoor = ({ r, i, isAdmin, onEdit }) => (
  <Link to={`/room/${r.id}`} data-testid={`lobby-room-card-${r.order}`} style={{ animationDelay: `${i * 60}ms` }}
    className="door rise group relative block overflow-hidden rounded-md border border-[#2d2d38] aspect-[3/4] bg-black">
    <img src={ROOM_ART[r.order]} alt="" className="absolute inset-0 h-full w-full object-cover opacity-75 transition-transform duration-700 group-hover:scale-105" />
    <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-black/0" />
    <div className="absolute inset-2.5 rounded-[3px] border border-white/10 pointer-events-none" />
    <div className={`absolute inset-x-0 top-0 h-1 ${r.on_air ? "bg-pink-500 glow-pink" : "bg-yellow-400/0 group-hover:bg-yellow-400/80"} transition-colors`} />
    <div className="absolute left-4 right-4 top-4 flex items-center justify-between">
      <span className="mono text-xs text-zinc-300">STUDIO {String(r.order).padStart(2, "0")}</span>
      <OnAirSign on={r.on_air} size="sm" testId={`room-on-air-${r.order}`} />
    </div>
    {isAdmin && (
      <button onClick={(e) => { e.preventDefault(); onEdit(r); }} data-testid={`edit-room-btn-${r.order}`}
        className="absolute right-4 top-12 grid h-8 w-8 place-items-center rounded-[3px] bg-black/70 border border-zinc-700 opacity-0 group-hover:opacity-100 transition-opacity hover:border-yellow-400">
        <Pencil className="h-3.5 w-3.5" />
      </button>
    )}
    <div className="absolute inset-x-4 bottom-4">
      {r.genre && <span className="mono-label text-yellow-300">{r.genre}</span>}
      <h3 className="text-2xl font-black tracking-tight" data-testid={`room-name-${r.order}`}>{r.name}</h3>
      {r.on_air && <p className="text-sm text-pink-300 truncate">Now: {r.show?.title}</p>}
      {!r.on_air && <p className="text-xs text-zinc-400 line-clamp-2">{r.description}</p>}
      <div className="mt-3 flex items-center justify-between">
        <span className="flex items-center gap-1.5 text-xs text-zinc-300" data-testid={`room-people-${r.order}`}>
          <span className={`h-1.5 w-1.5 rounded-full ${r.people ? "bg-[#39ff6a] glow-green" : "bg-zinc-600"}`} />{r.people} inside
        </span>
        <span className="flex items-center gap-1 text-xs font-semibold text-yellow-300 group-hover:gap-2 transition-all">
          {r.locked ? <><Lock className="h-3.5 w-3.5" /> Locked</> : <>Enter <ArrowRight className="h-3.5 w-3.5" /></>}
        </span>
      </div>
    </div>
  </Link>
);

const ShowRow = ({ s, rooms, staff }) => (
  <div className="flex items-center gap-4 py-3 border-b border-[#2d2d38] last:border-0" data-testid={`show-row-${s.id}`}>
    <div className="min-w-0 flex-1">
      <p className="font-semibold truncate">{s.title}</p>
      <p className="mono text-[11px] text-zinc-400">{new Date(s.scheduled_at).toLocaleString([], { weekday: "short", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })} · {rooms[s.room_id] || s.room_id} · {s.host_name}</p>
    </div>
    {s.has_access ? <span className="mono text-xs text-[#39ff6a]" data-testid={`show-access-${s.id}`}>✓ {staff ? "Staff access" : s.price > 0 ? "Ticket held" : "Free"}</span>
      : <BuyButton kind="ticket" itemId={s.id} price={s.price} testId={`buy-ticket-${s.id}`} className="h-8 text-xs" />}
  </div>
);

export default function Lobby() {
  const api = useApi();
  const { me, isStaff, isAdmin } = useMe();
  const [data, setData] = useState(null);
  const [editRoom, setEditRoom] = useState(null);
  const [showOpen, setShowOpen] = useState(false);
  const [courseOpen, setCourseOpen] = useState(false);
  const [courseId, setCourseId] = useState(null);
  const load = useCallback(() => api.get("/lobby").then((r) => setData(r.data)).catch(loadFailed("the lobby")), [api]);
  useEffect(() => { load(); const t = setInterval(load, 8000); return () => clearInterval(t); }, [load]);
  if (!data) return <div className="min-h-[60vh] grid place-items-center mono-label animate-pulse" data-testid="lobby-loading">Opening the lobby…</div>;
  const roomNames = Object.fromEntries(data.rooms.map((r) => [r.id, r.name]));
  const live = data.rooms.filter((r) => r.on_air).length;
  const inside = data.rooms.reduce((a, r) => a + r.people, 0) + data.lesson_room_people;
  return (
    <div className="spotlight min-h-screen" data-testid="lobby-page">
      <div className="mx-auto max-w-[1600px] px-4 md:px-8 py-10 space-y-12">
        <section className="flex flex-wrap items-end gap-6">
          <div className="rise">
            <span className="mono-label text-yellow-300">The Lobby</span>
            <h1 className="mt-2 text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight">Pick a door, {me.name?.split(" ")[0]}.</h1>
            <p className="mt-3 text-zinc-400 max-w-xl">Eight stage rooms and a private Lesson Room. <span className="text-pink-300">{live} on air</span> · <span className="text-[#39ff6a]">{inside} people inside</span>.</p>
          </div>
          {!me.is_member && (
            <div className="ml-auto panel p-5 flex items-center gap-5 glow-yellow" data-testid="membership-banner">
              <div><p className="font-bold">Camp membership</p><p className="text-sm text-zinc-400">All rooms, lessons and the members' library</p></div>
              <BuyButton kind="membership" price={me.membership_price} label={`Join · ${money(me.membership_price)}/mo`} testId="lobby-join-membership-btn" />
            </div>
          )}
        </section>

        <section>
          <div className="mb-4 flex items-center gap-2"><DoorOpen className="h-4 w-4 text-yellow-400" /><h2 className="text-lg font-bold">Stage rooms</h2></div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {data.rooms.map((r, i) => <RoomDoor key={r.id} r={r} i={i} isAdmin={isAdmin} onEdit={setEditRoom} />)}
          </div>
        </section>

        <div data-testid="lobby-lesson-room-card" className="door block panel p-6 md:p-8 relative overflow-hidden">
          <div className="absolute -right-10 -top-10 h-56 w-56 rounded-full bg-[#39ff6a]/10 blur-3xl" />
          <div className="flex flex-wrap items-center gap-6">
            <GraduationCap className="h-10 w-10 text-[#39ff6a]" />
            <div className="flex-1 min-w-[220px]">
              <span className="mono-label">Studio 09 · Private</span>
              <h3 className="text-2xl font-black">Virtual Lesson Room</h3>
              <p className="text-sm text-zinc-400">Face-to-face lessons with a plan, chord dictionary, metronome and shared tracks on screen.</p>
            </div>
            <div className="flex flex-wrap gap-2">
              {data.lessons.slice(0, 3).map((l) => (
                <Link key={l.id} to={`/lesson/${l.id}`} data-testid={`join-lesson-btn-${l.id}`}
                  className="rounded-[3px] bg-[#39ff6a] px-4 py-2 text-sm font-bold text-black hover:brightness-110">Join lesson: {l.title}</Link>
              ))}
              {!data.lessons.length && <span className="mono text-xs text-zinc-500">No lessons open for you right now</span>}
              <Link to="/lesson" data-testid="open-lesson-room-btn" className="rounded-[3px] border border-[#39ff6a]/60 px-4 py-2 text-sm font-semibold text-[#39ff6a] hover:bg-[#39ff6a]/10">Open Lesson Room</Link>
            </div>
          </div>
        </div>

        <ShowCalendar shows={data.upcoming_shows} rooms={roomNames} />

        <div className="grid gap-8 lg:grid-cols-2">
          <section className="panel p-5" data-testid="upcoming-shows">
            <div className="flex items-center gap-2 mb-2">
              <CalendarClock className="h-4 w-4 text-pink-400" /><h2 className="text-lg font-bold">Upcoming shows</h2>
              {isStaff && <button onClick={() => setShowOpen(true)} data-testid="schedule-show-btn" className="ml-auto flex items-center gap-1 text-sm text-yellow-300 hover:text-yellow-200"><Plus className="h-4 w-4" /> Schedule</button>}
            </div>
            {data.upcoming_shows.length === 0 && <p className="text-sm text-zinc-500 py-4">No shows scheduled yet.</p>}
            {data.upcoming_shows.map((s) => <ShowRow key={s.id} s={s} rooms={roomNames} staff={isStaff} />)}
          </section>
          <section className="panel p-5" data-testid="courses-list">
            <div className="flex items-center gap-2 mb-2">
              <BookOpen className="h-4 w-4 text-yellow-400" /><h2 className="text-lg font-bold">Courses</h2>
              {isStaff && <button onClick={() => setCourseOpen(true)} data-testid="new-course-btn" className="ml-auto flex items-center gap-1 text-sm text-yellow-300 hover:text-yellow-200"><Plus className="h-4 w-4" /> New course</button>}
            </div>
            {data.courses.length === 0 && <p className="text-sm text-zinc-500 py-4">No courses yet.</p>}
            {data.courses.map((c) => (
              <button key={c.id} onClick={() => setCourseId(c.id)} data-testid={`course-row-${c.id}`} className="w-full text-left flex items-center gap-4 py-3 border-b border-[#2d2d38] last:border-0 hover:bg-white/[.02]">
                <div className="min-w-0 flex-1"><p className="font-semibold truncate">{c.title}</p><p className="mono text-[11px] text-zinc-400"><Users className="inline h-3 w-3" /> {c.teacher_name} · {c.lessons_count} lessons</p></div>
                {c.has_access ? <span className="mono text-xs text-[#39ff6a]">✓ Enrolled</span> : <span className="mono font-bold text-yellow-300">{c.price > 0 ? money(c.price) : "Free"}</span>}
              </button>
            ))}
          </section>
        </div>
      </div>
      {editRoom && <EditRoomDialog room={editRoom} onClose={() => setEditRoom(null)} onSaved={load} />}
      <ScheduleShowDialog open={showOpen} onOpenChange={setShowOpen} rooms={data.rooms} onSaved={load} />
      <CourseDialog open={courseOpen} onOpenChange={setCourseOpen} rooms={data.rooms} onSaved={load} />
      {courseId && <CourseDetailDialog courseId={courseId} onClose={() => setCourseId(null)} onChanged={load} />}
    </div>
  );
}
